import { useState, useCallback } from 'react';
import * as patientService from '../services/patientService';
import { useUIStore } from '../stores/uiStore';
import { notifyApiError } from '../services/core/apiErrorUX';
import { notifyApiSuccess } from '../services/core/apiSuccessUX';
import {
  ICreatePatientRequest,
  IPatientMergeRollbackRequest,
  IPatientMergeRequest,
  IPatientMutationRequest,
} from '../types/interfaces/IPatient';

// Hook específico para pacientes
export function usePatients() {
  const [loading, setLoading] = useState(false);
  const { addNotification } = useUIStore();

  const getAllPatients = useCallback(async (institutionId: string) => {
    setLoading(true);
    try {
      const response = await patientService.getPatients(institutionId);

      if (response.success && response.data) {
        return response;
      } else {
        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al cargar pacientes',
        });
        return response;
      }
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Error al cargar pacientes',
      });
      return {
        success: false,
        data: null,
        error: 'Error al cargar pacientes',
      };
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  const getPatientById = useCallback(
    async (id: string, institutionId: string) => {
      setLoading(true);
      try {
        const response = await patientService.getPatientById(id, institutionId);

        if (response.success && response.data) {
          return response;
        } else {
          notifyApiError(addNotification, response, {
            context: 'module',
            fallbackMessage: 'Error al cargar paciente',
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al cargar paciente',
        });
        return {
          success: false,
          data: null,
          error: 'Error al cargar paciente',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const createPatient = useCallback(
    async (patientData: ICreatePatientRequest) => {
      setLoading(true);
      try {
        const response = await patientService.createPatient(patientData);

        if (response.success && response.data) {
          notifyApiSuccess(addNotification, 'Paciente Creado', 'Paciente creado correctamente');
          return response;
        } else {
          notifyApiError(addNotification, response, {
            context: 'module',
            fallbackMessage: 'Error al crear paciente',
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al crear paciente',
        });
        return {
          success: false,
          data: null,
          error: 'Error al crear paciente',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const searchPatients = useCallback(
    async (query: string, institutionId: string) => {
      setLoading(true);
      try {
        const response = await patientService.searchPatients(query, institutionId);

        if (response.success && response.data) {
          return response;
        }

        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'Error al buscar pacientes',
        });
        return response;
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al buscar pacientes',
        });
        return {
          success: false,
          data: null,
          error: 'Error al buscar pacientes',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const updatePatient = useCallback(
    async (id: string, patientData: IPatientMutationRequest) => {
      setLoading(true);
      try {
        const response = await patientService.updatePatient(id, patientData);

        if (response.success && response.data) {
          notifyApiSuccess(addNotification, 'Paciente Actualizado', 'Paciente actualizado correctamente');
          return response;
        } else {
          notifyApiError(addNotification, response, {
            context: 'module',
            fallbackMessage: 'Error al actualizar paciente',
          });
          return response;
        }
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'Error al actualizar paciente',
        });
        return {
          success: false,
          data: null,
          error: 'Error al actualizar paciente',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const mergePatients = useCallback(
    async (mergePayload: IPatientMergeRequest) => {
      setLoading(true);
      try {
        const response = await patientService.mergePatients(mergePayload);

        if (response.success && response.data) {
          notifyApiSuccess(addNotification, 'Pacientes fusionados', 'La reconciliacion se realizo correctamente');
          return response;
        }

        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'No se pudo fusionar pacientes',
        });
        return response;
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'No se pudo fusionar pacientes',
        });
        return {
          success: false,
          data: null,
          error: 'No se pudo fusionar pacientes',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const getPatientMergeHistory = useCallback(
    async (institutionId: string, take: number = 20) => {
      setLoading(true);
      try {
        const response = await patientService.getPatientMergeHistory(institutionId, take);

        if (response.success && response.data) {
          return response;
        }

        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'No se pudo cargar el historial de fusiones',
        });
        return response;
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'No se pudo cargar el historial de fusiones',
        });
        return {
          success: false,
          data: null,
          error: 'No se pudo cargar el historial de fusiones',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  const rollbackPatientMerge = useCallback(
    async (mergeLogId: string, payload: IPatientMergeRollbackRequest) => {
      setLoading(true);
      try {
        const response = await patientService.rollbackPatientMerge(mergeLogId, payload);

        if (response.success && response.data) {
          notifyApiSuccess(addNotification, 'Rollback aplicado', 'La fusión fue revertida correctamente');
          return response;
        }

        notifyApiError(addNotification, response, {
          context: 'module',
          fallbackMessage: 'No se pudo revertir la fusión',
        });
        return response;
      } catch (error) {
        addNotification({
          type: 'error',
          title: 'Error',
          message: 'No se pudo revertir la fusión',
        });
        return {
          success: false,
          data: null,
          error: 'No se pudo revertir la fusión',
        };
      } finally {
        setLoading(false);
      }
    },
    [addNotification]
  );

  return {
    getAllPatients,
    getPatientById,
    searchPatients,
    createPatient,
    updatePatient,
    mergePatients,
    getPatientMergeHistory,
    rollbackPatientMerge,
    loading,
  };
}
